import React from 'react';
import { Activity, Clock, TrendingUp, Zap } from 'lucide-react';
import FilterModalWrapper from './FilterModalWrapper';

/**
 * StatusModal - Desktop modal for selecting market type/game status
 * Handles different filtering options based on sport:
 * - Football: all, live, prematch (markets)
 * - NFL: all, live, upcoming (game status)
 * - MLB, NBA, etc: To be added later
 * Adapted from mobile MarketSheet
 *
 * @param {boolean} isOpen - Whether modal is open
 * @param {Function} onClose - Callback to close modal
 * @param {string} currentStatus - Currently selected status
 * @param {Function} onStatusChange - Callback when status is selected
 * @param {string} sport - Current sport context
 */
const StatusModal = ({
  isOpen,
  onClose,
  currentStatus,
  onStatusChange,
  sport = 'football'
}) => {
  // Different options based on sport
  const getStatusOptions = () => {
    switch (sport) {
      case 'nfl':
        // NFL uses game status filtering (live/upcoming)
        return [
          { id: 'all', label: 'All Games', icon: TrendingUp, description: 'Show all games' },
          { id: 'live', label: 'Live', icon: Zap, description: 'Games in progress', color: 'red' },
          { id: 'upcoming', label: 'Upcoming', icon: Clock, description: 'Games not started' }
        ];

      case 'football':
      default:
        // Football uses market type filtering (live/prematch)
        return [
          { id: 'all', label: 'All Markets', icon: TrendingUp, description: 'Show all available games' },
          { id: 'live', label: 'Live', icon: Activity, description: 'In-play games only' },
          { id: 'prematch', label: 'Pre-match', icon: Clock, description: 'Upcoming games only' }
        ];

      // Future sports can be added here:
      // case 'mlb':
      // case 'nba':
      // case 'ncaaf':
      // case 'ncaab':
    }
  };

  const statusOptions = getStatusOptions();

  const handleStatusSelect = (statusId) => {
    onStatusChange(statusId);
  };

  return (
    <FilterModalWrapper
      isOpen={isOpen}
      onClose={onClose}
      title={sport === 'nfl' ? 'Game Status' : 'Market Type'}
      icon={Activity}
      maxWidth="sm"
      maxHeight="50vh"
    >
      {/* Status Options */}
      <div className="p-4">
        {statusOptions.map((option) => {
          const Icon = option.icon;
          const isSelected = currentStatus === option.id;
          const isLive = option.id === 'live' && option.color === 'red'; // NFL live games

          return (
            <button
              key={option.id}
              onClick={() => handleStatusSelect(option.id)}
              className={`
                w-full flex items-center gap-3 p-3 rounded-lg transition-all mb-2
                ${isSelected
                  ? isLive
                    ? 'bg-red-500/20 border border-red-500/50'
                    : 'bg-emerald-500/20 border border-emerald-500/50'
                  : 'hover:bg-gray-800/50 border border-transparent'
                }
              `}
            >
              <div className={`
                p-2 rounded-lg
                ${isSelected
                  ? isLive ? 'bg-red-500/20' : 'bg-emerald-500/20'
                  : 'bg-gray-800'}
              `}>
                <Icon className={`
                  w-5 h-5
                  ${isSelected
                    ? isLive ? 'text-red-400' : 'text-emerald-400'
                    : 'text-gray-400'}
                `} />
              </div>

              <div className="flex-1 text-left">
                <div className={`
                  font-medium text-sm flex items-center gap-2
                  ${isSelected
                    ? isLive ? 'text-red-400' : 'text-emerald-400'
                    : 'text-white'}
                `}>
                  {option.label}
                  {isLive && isSelected && (
                    <div className="w-2 h-2 bg-red-500 rounded-full animate-pulse" />
                  )}
                </div>
                <div className="text-xs text-gray-500 mt-0.5">
                  {option.description}
                </div>
              </div>

              {isSelected && (
                <div className={`w-2 h-2 rounded-full ${isLive ? 'bg-red-400' : 'bg-emerald-400'}`} />
              )}
            </button>
          );
        })}
      </div>
    </FilterModalWrapper>
  );
};

export default StatusModal;
